/** Professional e-mail: the DNS status of a domain's MX, SPF, DKIM and DMARC records, and what the plan allows. */
import { useQuery } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";

import { authedPortal } from "./api";
import type { PillTone } from "./domains";

export type DnsKind = "mx" | "spf" | "dkim" | "dmarc";
export type DnsState = "required" | "pending" | "verified" | "failed" | "stale";

export interface EmailCheck {
  kind: DnsKind;
  state: DnsState;
  hostname: string;
  record_type: string;
  expected: string[];
  observed: string[];
  corrective_action: string;
  /** False when DNS could not be asked on the last refresh: this is the previous result. */
  reachable: boolean;
}

/** Whether a mailbox may be created on the domain now; when not, why and what to do. */
export interface EmailProvisioning {
  allowed: boolean;
  reason:
    | "DOMAIN_NOT_AUTHORISED"
    | "EMAIL_PROVIDER_UNAVAILABLE"
    | "EMAIL_NOT_ENTITLED"
    | "DNS_NOT_VERIFIED"
    | "MAILBOX_LIMIT_REACHED"
    | null;
  message: string | null;
  next_action: string | null;
}

export interface EmailEntitlement {
  plan: string;
  enabled: boolean;
  mailbox_limit: number;
  reason: string | null;
  /** A scheduled plan change will take mailboxes away on `ends_on`; until then nothing changes. */
  ending: boolean;
  ends_on: string | null;
  next_mailbox_limit: number | null;
}

export interface EligibleDomain {
  domain_id: string;
  fqdn: string;
}

export interface EmailDomain {
  id: string;
  domain_id: string;
  /** The domain folder it is in (null: unfiled): what the person's e-mail permission is checked against. */
  folder_id: string | null;
  fqdn: string;
  provider: string;
  state: DnsState;
  checks: EmailCheck[];
  confirmed: Partial<Record<DnsKind, string[]>>;
  last_checked_at: string | null;
  last_verified_at: string | null;
  stale: boolean;
  lookup_failed: boolean;
  /** A repeat inside the cooldown: this is the last check's result, DNS was not asked again. */
  recently_checked: boolean;
  provisioning: EmailProvisioning;
  entitlement: EmailEntitlement;
  operation_reference: string | null;
  /** Mailboxes in the workspace that take one of the plan's places. */
  mailboxes_used?: number;
}

export interface EmailOverview {
  domains: EmailDomain[];
  /** The providers this deployment has configured (none until one is approved). */
  providers: string[];
  entitlement: EmailEntitlement;
}

/** What the provider last confirmed about a mailbox (a change in flight never moves it). */
export type MailboxState = "provisioning" | "active" | "suspended" | "deleted";

/**
 * A change in flight: being made (`in_progress`), unanswered (`unknown`: it may or may not have happened, nothing is
 * repeated until we know) or refused in a way worth asking again (`failed`).
 */
export interface PendingChange {
  action: string;
  outcome: "in_progress" | "unknown" | "failed";
  reference: string | null;
  message: string | null;
  retryable: boolean;
}

export type MailboxActionName =
  | "update"
  | "suspend"
  | "resume"
  | "reset_password"
  | "delete"
  | "recover"
  | "add_alias"
  | "add_forwarding"
  | "retry";

export interface EmailItem {
  id: string;
  kind: "alias" | "forwarding";
  value: string;
  address: string;
  state: "provisioning" | "active" | "removing";
  last_confirmed_at: string | null;
  pending: PendingChange | null;
  /** Why it can't be removed now (a change in flight), or null. */
  remove_reason: string | null;
}

export interface Mailbox {
  id: string;
  email_domain_id: string;
  address: string;
  local_part: string;
  display_name: string;
  state: MailboxState;
  last_confirmed_at: string | null;
  created_at: string;
  deleted_at: string | null;
  /** A deleted mailbox can be recovered until then. */
  purge_after: string | null;
  pending: PendingChange | null;
  /** Every action, with the server's reason when it can't be done now (shown on the disabled button). */
  actions: Record<MailboxActionName, { allowed: boolean; reason: string | null }>;
  /** What deleting it would break: shown before it is confirmed. */
  delete_consequences: string[];
  aliases: EmailItem[];
  forwardings: EmailItem[];
}

export interface MailboxList {
  email_domain_id: string;
  fqdn: string;
  usage: { used: number; limit: number };
  provisioning: EmailProvisioning;
  limits: { aliases_per_mailbox: number; forwardings_per_mailbox: number };
  recovery_days: number;
  mailboxes: Mailbox[];
}

/** What came of a change. A refusal for good is an error instead. */
export interface MailboxChange {
  outcome: "done" | "in_progress" | "unknown" | "failed";
  operation_reference: string | null;
  message: string | null;
  mailbox: Mailbox | null;
  /** After a create or a reset that went through: shown this once, never stored anywhere. */
  one_time_password: string | null;
}

/** The lifecycle actions sent as `POST …/mailboxes/{id}/{path}`. */
export type MailboxCommand = "suspend" | "resume" | "reset-password" | "recover" | "retry";

const base = (orgId: string) => `/v1/organizations/${orgId}/email/domains`;
const boxes = (orgId: string) => `/v1/organizations/${orgId}/email/managed-mailboxes`;

export const emailApi = {
  overview: (orgId: string) => authedPortal<EmailOverview>(base(orgId)),
  eligible: (orgId: string) => authedPortal<{ domains: EligibleDomain[] }>(`${base(orgId)}/eligible`),
  connect: (orgId: string, domainId: string, provider: string) =>
    authedPortal<EmailDomain>(base(orgId), { method: "POST", body: { domain_id: domainId, provider } }),
  refresh: (orgId: string, emailDomainId: string) =>
    authedPortal<EmailDomain>(`${base(orgId)}/${emailDomainId}/refresh`, { method: "POST" }),
  mailboxes: (orgId: string, emailDomainId: string) =>
    authedPortal<MailboxList>(`${base(orgId)}/${emailDomainId}/mailboxes`),
  createMailbox: (orgId: string, emailDomainId: string, body: { local_part: string; display_name: string }) =>
    authedPortal<MailboxChange>(`${base(orgId)}/${emailDomainId}/mailboxes`, { method: "POST", body }),
  renameMailbox: (orgId: string, mailboxId: string, displayName: string) =>
    authedPortal<MailboxChange>(`${boxes(orgId)}/${mailboxId}`, {
      method: "PATCH",
      body: { display_name: displayName },
    }),
  deleteMailbox: (orgId: string, mailboxId: string, confirm: boolean) =>
    authedPortal<MailboxChange>(`${boxes(orgId)}/${mailboxId}${confirm ? "?confirm=true" : ""}`, {
      method: "DELETE",
    }),
  command: (orgId: string, mailboxId: string, command: MailboxCommand) =>
    authedPortal<MailboxChange>(`${boxes(orgId)}/${mailboxId}/${command}`, { method: "POST" }),
  addAlias: (orgId: string, mailboxId: string, localPart: string) =>
    authedPortal<MailboxChange>(`${boxes(orgId)}/${mailboxId}/aliases`, {
      method: "POST",
      body: { local_part: localPart },
    }),
  addForwarding: (orgId: string, mailboxId: string, target: string) =>
    authedPortal<MailboxChange>(`${boxes(orgId)}/${mailboxId}/forwardings`, {
      method: "POST",
      body: { target },
    }),
  removeItem: (orgId: string, mailboxId: string, itemId: string) =>
    authedPortal<MailboxChange>(`${boxes(orgId)}/${mailboxId}/items/${itemId}`, { method: "DELETE" }),
};

export const emailKeys = {
  all: (orgId: string) => ["email", orgId] as const,
  overview: (orgId: string) => ["email", orgId, "overview"] as const,
  eligible: (orgId: string) => ["email", orgId, "eligible"] as const,
  mailboxes: (orgId: string, emailDomainId: string) => ["email", orgId, "mailboxes", emailDomainId] as const,
};

export function useMailboxes(emailDomainId: string) {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: emailKeys.mailboxes(orgId, emailDomainId),
    queryFn: () => emailApi.mailboxes(orgId, emailDomainId),
  });
}

/** A mailbox's confirmed state as text and tone (the text carries the meaning; colour is never the only cue). */
export const MAILBOX_PILL: Record<MailboxState, { label: string; tone: PillTone }> = {
  provisioning: { label: "Being set up", tone: "info" },
  active: { label: "Active", tone: "success" },
  suspended: { label: "Suspended", tone: "warning" },
  deleted: { label: "Deleted", tone: "neutral" },
};

/** A change in flight, in the customer's words. */
export const PENDING_LABEL: Record<PendingChange["outcome"], string> = {
  in_progress: "Change in progress",
  unknown: "Change not confirmed yet",
  failed: "Change needs a retry",
};

/** What each change is called on a button and in an announcement. */
export const CHANGE_LABEL: Record<string, string> = {
  create: "Create",
  update: "Rename",
  suspend: "Suspend",
  resume: "Resume",
  reset_password: "Reset password",
  delete: "Delete",
  recover: "Recover",
  purge: "Permanently delete",
};

export function useEmailOverview() {
  const { orgId } = useOrganization();
  return useQuery({ queryKey: emailKeys.overview(orgId), queryFn: () => emailApi.overview(orgId) });
}

export const KIND_LABEL: Record<DnsKind, string> = { mx: "MX", spf: "SPF", dkim: "DKIM", dmarc: "DMARC" };
/** What each record does, in the customer's words. */
export const KIND_PURPOSE: Record<DnsKind, string> = {
  mx: "Sends your mail to the mail service",
  spf: "Says who may send mail as your domain",
  dkim: "Signs your mail so it can be trusted",
  dmarc: "Says what to do with mail that fails the checks",
};

/** The label and tone of a state. The label always carries the meaning: colour is never the only cue. */
export const STATE_PILL: Record<DnsState, { label: string; tone: PillTone }> = {
  verified: { label: "Verified", tone: "success" },
  pending: { label: "Waiting for DNS", tone: "info" },
  required: { label: "Record needed", tone: "warning" },
  failed: { label: "Needs attention", tone: "danger" },
  stale: { label: "Out of date", tone: "neutral" },
};

/** The server's refusal reasons for connecting e-mail, with the next step to show beside them. */
export function connectProblem(error: unknown): { message: string; nextAction: string | null } | null {
  const e = error as { message?: string; details?: Record<string, unknown> } | null;
  if (!e?.message) return null;
  const next = e.details?.next_action;
  return { message: e.message, nextAction: typeof next === "string" ? next : null };
}

/** The domains this person may connect to e-mail (needs `email:manage`; the server has already filtered them). */
export function useEligibleDomains(enabled: boolean) {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: emailKeys.eligible(orgId),
    queryFn: () => emailApi.eligible(orgId),
    enabled,
  });
}
